import React, { useState, useRef } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Modal, Animated,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { theme } from '../Common.style';

export default function GameRoleRevealScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const {
    room, currentRound, currentUserId, amImpostor, mySecretWord,
    acknowledgeRole, localPlayers,
  } = useGame();

  const isLocal = room?.mode === 'LOCAL';
  const [localIdx, setLocalIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [holding, setHolding] = useState(false);
  const holdTimer = useRef(null);
  const holdProgress = useRef(new Animated.Value(0)).current;

  if (!room || !currentRound) {
    navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    return null;
  }

  // ── Local mode: pass-and-play ─────────────────────────────────────────────
  if (isLocal) {
    const totalPlayers = localPlayers.length;
    const currentPlayer = localPlayers[localIdx];
    const isImpostor = localIdx === currentRound.impostorId;

    const startHold = () => {
      setHolding(true);
      holdProgress.setValue(0);
      Animated.timing(holdProgress, { toValue: 1, duration: 3000, useNativeDriver: false }).start(({ finished }) => {
        if (finished) {
          setHolding(false);
          setRevealed(false);
          const nextIdx = localIdx + 1;
          if (nextIdx >= totalPlayers) {
            navigation.navigate(SCREEN_NAMES.GAME_CLUE_PHASE);
          } else {
            setLocalIdx(nextIdx);
          }
        }
      });
    };

    const cancelHold = () => {
      setHolding(false);
      holdProgress.stopAnimation();
      holdProgress.setValue(0);
    };

    if (!revealed) {
      return (
        <View style={styles.container}>
          <Text style={styles.passLabel}>PASSE LE TÉLÉPHONE</Text>
          <View style={styles.passCard}>
            <Text style={styles.passTo}>À {currentPlayer?.name ?? `Joueur ${localIdx + 1}`}</Text>
            <Text style={styles.passInstruction}>Appuie pour voir ton rôle en privé</Text>
            <Text style={styles.progress}>{localIdx + 1}/{totalPlayers} joueurs</Text>
          </View>
          <TouchableOpacity style={styles.revealBtn} onPress={() => setRevealed(true)}>
            <Text style={styles.revealBtnText}>Voir mon rôle</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={[styles.container, isImpostor ? styles.impostorBg : styles.villagerBg]}>
        <Text style={styles.roleLabel}>{isImpostor ? '🕵️ IMPOSTEUR' : '🧑 VILLAGEOIS'}</Text>
        {isImpostor ? (
          <Text style={styles.wordDisplay}>Mot secret : ???</Text>
        ) : (
          <Text style={styles.wordDisplay}>Mot secret : {currentRound.secretWord}</Text>
        )}
        <Text style={styles.holdHint}>Maintiens pour masquer et passer</Text>
        <Animated.View style={[styles.holdBar, {
          width: holdProgress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
        }]} />
        <TouchableOpacity
          style={styles.dismissBtn}
          onPressIn={startHold}
          onPressOut={cancelHold}
          activeOpacity={0.8}
        >
          <Text style={styles.dismissBtnText}>Maintenir pour continuer</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // ── Co-présent / Remote mode ───────────────────────────────────────────────
  if (!revealed) {
    return (
      <View style={styles.container}>
        <Text style={styles.passLabel}>DÉCOUVREZ VOTRE RÔLE</Text>
        <Text style={styles.privateHint}>Regardez votre écran en privé</Text>
        <TouchableOpacity style={styles.revealBtn} onPress={() => setRevealed(true)}>
          <Text style={styles.revealBtnText}>Voir mon rôle</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const handleDismiss = async () => {
    try {
      await acknowledgeRole();
      navigation.navigate(SCREEN_NAMES.GAME_CLUE_PHASE);
    } catch (e) {
      toast.show(e.message || 'Erreur', { type: 'danger' });
    }
  };

  return (
    <View style={[styles.container, amImpostor ? styles.impostorBg : styles.villagerBg]}>
      <Text style={styles.roleLabel}>{amImpostor ? '🕵️ IMPOSTEUR' : '🧑 VILLAGEOIS'}</Text>
      {amImpostor ? (
        <>
          <Text style={styles.wordDisplay}>Mot secret : ???</Text>
          <Text style={styles.impostorTip}>
            Donne des indices vagues. Évite de te faire repérer. Si démasqué, tu peux encore gagner en devinant le mot.
          </Text>
        </>
      ) : (
        <>
          <Text style={styles.wordDisplay}>Mot secret</Text>
          <Text style={styles.secretWord}>{mySecretWord}</Text>
          <Text style={styles.villagerTip}>
            Donne un indice lié au mot sans le révéler. Repère l'imposteur grâce aux indices louches.
          </Text>
        </>
      )}
      <TouchableOpacity style={styles.readyBtn} onPress={handleDismiss}>
        <Text style={styles.readyBtnText}>Prêt !</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', padding: 24 },
  impostorBg: { backgroundColor: '#1a1a2e' },
  villagerBg: { backgroundColor: '#0f3460' },
  passLabel: { fontSize: 22, fontWeight: '900', letterSpacing: 2, color: '#333', textAlign: 'center', marginBottom: 16 },
  privateHint: { color: '#666', marginBottom: 32, textAlign: 'center' },
  passCard: { backgroundColor: '#f5f5f5', borderRadius: 16, padding: 24, alignItems: 'center', marginBottom: 32, width: '100%' },
  passTo: { fontSize: 26, fontWeight: '800', color: '#333', marginBottom: 8 },
  passInstruction: { color: '#888', fontSize: 14 },
  progress: { marginTop: 12, color: theme.colors.primary, fontWeight: '600' },
  revealBtn: { backgroundColor: theme.colors.primary, paddingHorizontal: 40, paddingVertical: 16, borderRadius: 14 },
  revealBtnText: { color: '#fff', fontWeight: '700', fontSize: 17 },
  roleLabel: { fontSize: 32, fontWeight: '900', color: '#fff', marginBottom: 20, textAlign: 'center' },
  wordDisplay: { color: 'rgba(255,255,255,0.7)', fontSize: 16, marginBottom: 8 },
  secretWord: { fontSize: 48, fontWeight: '900', color: '#fff', marginBottom: 16, textAlign: 'center' },
  impostorTip: { color: 'rgba(255,255,255,0.6)', textAlign: 'center', fontSize: 14, marginBottom: 32, lineHeight: 22 },
  villagerTip: { color: 'rgba(255,255,255,0.6)', textAlign: 'center', fontSize: 14, marginBottom: 32, lineHeight: 22 },
  holdHint: { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 16, marginTop: 12 },
  holdBar: { height: 4, backgroundColor: '#fff', borderRadius: 2, alignSelf: 'flex-start', marginBottom: 20 },
  dismissBtn: { backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 32, paddingVertical: 14, borderRadius: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)' },
  dismissBtnText: { color: '#fff', fontWeight: '600', fontSize: 15 },
  readyBtn: { backgroundColor: '#fff', paddingHorizontal: 40, paddingVertical: 16, borderRadius: 14 },
  readyBtnText: { color: theme.colors.primary, fontWeight: '700', fontSize: 17 },
});
